<template>
  <AppMainDrawer>
    <template #drawer>
      <DrawerStyled v-if="pageHasFilters[route.name]">
        <template #drawer-content>
          <ExpandableFilters />
        </template>
      </DrawerStyled>
    </template>

    <template #content>
      <div class="job-architecture-page">
        <TopBarForDrawer>
          <template #page-title>
            <TopBarForDrawerTitle
              :page-title="route.meta.title"
              :page-description="pageDescription"
              :is-loading="pageLoading"
            />
          </template>
          <template #summaries>
            <JobArchitectureSummaries
              :totals="architectureTotals"
              :can-edit="canEditArchitecture"
            />
          </template>
          <template #topbar-actions>
            <JobArchitectureTopbarActions
              :can-edit="canEditArchitecture"
              :has-results="hasListResults"
              @download="handleDownloadCsv(filteredTreeData)"
              @compare="openCompare"
            />
          </template>
        </TopBarForDrawer>

        <div class="job-architecture-list-wrapper">
          <JobArchitectureListView
            :key="listTreeExpandKey"
            v-model:selected-profiles="listSelectedProfileUlids"
            :tree-data="filteredTreeData"
            :can-edit="canEditArchitecture"
          />
        </div>
        <JobArchitectureRowActionModals v-if="canEditArchitecture" />
      </div>
    </template>
  </AppMainDrawer>
</template>

<script setup>
import {
  computed,
  ref,
  nextTick,
  onMounted,
  onBeforeUnmount,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { storeToRefs } from 'pinia';
import { useJobArchitecture } from '@/stores/jobArchitecture/jobArchitecture';
import {
  useJobArchitectureFilters,
  mergeJobArchitectureRouteQuery,
} from '@/composables/useJobArchitectureFilters';
import { useFilters } from '@/stores/filters/filters';
import { useAppBase } from '@/stores/app/base';
import { useAppLoading } from '@/stores/app/loading';
import { useAppRoute } from '@/stores/app/route';
import { subscribeToFilterUpdates, unsubscribeToFilterUpdates } from '@/helpers/filterDispatcher';
import { pageDescriptions } from '@/helpers/constants/pageDescriptions';
import { tooltipKeys } from '@/helpers/constants/tooltipKeys';
import { useTooltips } from '@/stores/insights/tooltips';
import { useTracking } from '@/stores/tracking/tracking';
import AppMainDrawer from '@/components/shared/AppMainDrawer.vue';
import DrawerStyled from '@/components/shared/DrawerStyled.vue';
import TopBarForDrawer from '@/components/shared/topbar/TopBarForDrawer.vue';
import TopBarForDrawerTitle from '@/components/shared/topbar/TopBarForDrawerTitle.vue';
import ExpandableFilters from '@/components/filter/ExpandableFilters.vue';
import JobArchitectureListView from '@/components/jobArchitecture/JobArchitectureListView.vue';
import JobArchitectureSummaries from '@/components/jobArchitecture/JobArchitectureSummaries.vue';
import JobArchitectureTopbarActions from '@/components/jobArchitecture/JobArchitectureTopbarActions.vue';
import JobArchitectureRowActionModals from '@/components/jobArchitecture/JobArchitectureRowActionModals.vue';
import { useAppAuthData } from '@/stores/app/authData';
import { collectExpandedNodeIdsForProfileUlids } from '@/components/jobArchitecture/helpers';

const route = useRoute();
const router = useRouter();
const { previousRoute } = storeToRefs(useAppRoute());

const listTreeExpandKey = ref(0);

const pageDescription = computed(() => pageDescriptions()[route.name] || '');

const { mergedTempFilters } = storeToRefs(useFilters());
const { pageHasFilters } = storeToRefs(useAppBase());
const { pageLoading } = storeToRefs(useAppLoading());

const { loadTooltips } = useTooltips();

const { setFilterConfig, clearPersistentFilters } = useFilters();
const { showFilterBarSettings } = useAppBase();

const authData = useAppAuthData();
const { isStandaloneJobNav } = storeToRefs(authData);
const { checkRbacPermissions } = authData;

const canEditArchitecture = computed(
  () => isStandaloneJobNav.value && checkRbacPermissions(['editJobArchitecture']),
);

const jobArchitectureStore = useJobArchitecture();
const { selectedProfileUlids } = storeToRefs(jobArchitectureStore);
const {
  clearSelection,
  loadTree,
  downloadArchitectureCsv,
  setSelectedProfileUlids,
  clearSelectedProfileUlids,
  clearListFilterSnapshot,
  setListDefaultExpandedNodeIds,
  clearListDefaultExpandedNodeIds,
  resetEditMode,
} = jobArchitectureStore;

const listSelectedProfileUlids = computed({
  get: () => selectedProfileUlids.value,
  set: (ulids) => setSelectedProfileUlids(ulids),
});

const {
  initializeFilters,
  filteredTreeData,
  clearSearch,
  reloadPage,
  saveListFilterSnapshotForCompare,
  searchText,
  selectedAlertCategoryIds,
  getAlertFilterExpansionIds,
} = useJobArchitectureFilters();

const architectureTotals = computed(() => {
  const groups = filteredTreeData.value?.groups || [];
  const families = groups.flatMap((group) => group.families || []);
  const profiles = families.flatMap((family) => family.profiles || []);

  return {
    groups: groups.length,
    families: families.length,
    profiles: profiles.length,
  };
});

const hasListResults = computed(() => architectureTotals.value.groups > 0);

const handleDownloadCsv = (treeData) => {
  useTracking().jaListDownloadedCsv();

  downloadArchitectureCsv(treeData);
};

const openCompare = () => {
  if (!selectedProfileUlids.value.length) return;

  useTracking().jaListClickedCompare();

  saveListFilterSnapshotForCompare();

  router.push({
    name: 'job-architecture-compare',
    query: {
      profiles: selectedProfileUlids.value.join(','),
      from: 'job-architecture',
    },
  });
};

const syncListTreeExpansion = async () => {
  await nextTick();

  const tree = filteredTreeData.value;
  let expanded = null;

  if (selectedAlertCategoryIds.value.length && tree?.groups?.length) {
    expanded = getAlertFilterExpansionIds();
  } else if (selectedProfileUlids.value.length && tree?.groups?.length) {
    expanded = collectExpandedNodeIdsForProfileUlids(tree, selectedProfileUlids.value);
  } else if (
    route.query.preview
    && ['jobProfileHris', 'jobProfile'].includes(route.query.type)
    && tree?.groups?.length
  ) {
    expanded = collectExpandedNodeIdsForProfileUlids(tree, [route.query.preview]);
  }

  setListDefaultExpandedNodeIds(expanded);
  listTreeExpandKey.value += 1;
};

const loadPage = async () => {
  reloadPage({
    router,
    routeQuery: route.query,
    mergedTempFilters: mergedTempFilters.value,
    applySidebarFilters: true,
  });

  await syncListTreeExpansion();
};

onMounted(async () => {
  useTracking().trackPageLoad({ page: 'Job Architecture: Open' });

  if (previousRoute.value?.name !== 'job-architecture-compare') {
    clearSelectedProfileUlids();
    clearListFilterSnapshot();
  }

  subscribeToFilterUpdates(loadPage);

  setFilterConfig({
    module: 'jobArchitecture',
    showMyJobs: false,
    datepicker: false,
  });

  showFilterBarSettings({ route: route.name });

  loadTooltips(tooltipKeys['job-architecture']);

  clearSelection();

  await loadTree();

  const familyUlid = route.query.family;

  if (familyUlid) {
    const { family: _drop, ...queryRest } = route.query;

    router.replace({
      name: 'job-architecture-family',
      params: { familyUlid },
      query: queryRest,
    });

    return;
  }

  const restoredFromSnapshot = initializeFilters(route.query);

  if (restoredFromSnapshot) {
    router.replace({
      query: mergeJobArchitectureRouteQuery(
        route.query,
        mergedTempFilters.value,
        searchText.value,
      ),
    }).catch(() => true);
  }

  // Expand nodes that contain the selected profile ULIDs when returning from compare with a selection.
  await syncListTreeExpansion();
});

onBeforeUnmount(() => {
  unsubscribeToFilterUpdates();

  resetEditMode();

  clearPersistentFilters();

  clearSearch();

  clearListDefaultExpandedNodeIds();

  setFilterConfig({
    subMenuType: null,
  });
});
</script>

<style scoped lang="less">
@import '@/less/mixins';

.job-architecture-page {
  display: flex;
  flex-direction: column;
  width: 100%;

  @media screen and (min-width: 1800px) {
    max-width: 1400px;
  }
}

.job-architecture-list-wrapper {
  flex-shrink: 0;
  margin: 0 24px 16px;
  border: 1px solid @stroke-default;
  border-top: none;
  border-radius: 8px;
  background: @bg-default;
}

.job-architecture-page :deep(.topbar-actions) {
  width: 100%;
  min-height: 40px;
}
</style>
